export interface DraftSection {
  title: string
  content: string
}

/**
 * Parses markdown assistant response to extract actual draft/story prose copy
 * separate from outlines, plans, and conversational intros/outros.
 */
export function parseDraftSections(text: string): DraftSection[] {
  const sections: DraftSection[] = []
  const lines = text.split(/\r?\n/)

  let currentTitle: string | null = null
  let currentContentLines: string[] = []

  const isDraftHeader = (line: string): boolean => {
    const clean = line.trim().toLowerCase()
    if (
      clean.includes('outline') ||
      clean.includes('plan') ||
      clean.includes('suggestion') ||
      clean.includes('structure')
    ) {
      return false
    }
    const keywords = ['draft', 'story', 'prose', 'copy', 'manuscript']
    const hasKeyword = keywords.some((k) => clean.includes(k))
    if (!hasKeyword) return false

    return (
      clean.startsWith('#') ||
      (clean.startsWith('**') && clean.endsWith('**')) ||
      (clean.startsWith('**') && clean.includes('**:')) ||
      (clean.endsWith(':') && clean.includes('**'))
    )
  }

  const isOutroLine = (line: string): boolean => {
    const clean = line.trim().toLowerCase()
    return (
      clean.startsWith('let me know') ||
      clean.startsWith('hope this') ||
      clean.startsWith('what do you think') ||
      clean.startsWith('we can expand') ||
      clean.startsWith('feel free to')
    )
  }

  const isOtherHeader = (line: string): boolean => {
    const clean = line.trim().toLowerCase()
    // A header that is NOT a draft header (e.g., outline, next steps, feedback)
    const isHeading = clean.startsWith('#')
    const isBoldHeader =
      (clean.startsWith('**') && clean.endsWith('**')) ||
      (clean.startsWith('**') && clean.includes('**:'))
    return (isHeading || isBoldHeader) && !isDraftHeader(line)
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    if (isDraftHeader(line)) {
      if (currentTitle && currentContentLines.length > 0) {
        sections.push({
          title: currentTitle,
          content: currentContentLines.join('\n').trim()
        })
      }
      currentTitle = line.replace(/[#*:]/g, '').trim()
      currentContentLines = []
    } else if (currentTitle) {
      if (isOutroLine(line) || isOtherHeader(line)) {
        sections.push({
          title: currentTitle,
          content: currentContentLines.join('\n').trim()
        })
        currentTitle = null
        currentContentLines = []
      } else {
        currentContentLines.push(line)
      }
    }
  }

  if (currentTitle && currentContentLines.length > 0) {
    sections.push({
      title: currentTitle,
      content: currentContentLines.join('\n').trim()
    })
  }

  return sections
}

export interface ContentBlock {
  type: 'chat' | 'story'
  title?: string
  content: string
}

export interface ParseMessageBlocksOptions {
  /** The mode the reply was produced in — the user message that prompted it records this. */
  mode?: 'plan' | 'ask' | 'writing' | 'edit'
}

export function parseMessageBlocks(
  text: string,
  options: ParseMessageBlocksOptions = {}
): ContentBlock[] {
  // Ask mode is read-only research: whatever the text looks like, it is never a draft the writer
  // can insert. The server strips well-formed [STORY] pairs from Ask replies, but an opening tag
  // the model never closed slipped past it (and past the tag branch below, which promotes
  // everything after an unclosed tag to a story block). Bypassing both heuristics here also
  // fixes replies already stored with the marker.
  if (options.mode === 'ask') {
    const content = text
      .replace(/\[\/?STORY\]/gi, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
    return content.length > 0 ? [{ type: 'chat', content }] : []
  }

  const blocks: ContentBlock[] = []

  const isRealStoryBlock = (content: string): boolean => {
    const trimmed = content.trim()
    if (trimmed.length === 0) return false

    // If it's very short, check if it looks like a conversational quote or mention
    if (trimmed.length < 25) {
      const clean = trimmed.toLowerCase()
      // If it contains only words like 'and', 'or', 'to', 'tags', 'tag', 'label', 'format'
      const words = clean.split(/[^a-z]+/).filter(Boolean)
      const conversationalWords = [
        'and',
        'or',
        'to',
        'tags',
        'tag',
        'label',
        'here',
        'the',
        'with',
        'in',
        'format',
        'excerpts',
        'excerpt'
      ]
      const isAllConversational = words.every((w) => conversationalWords.includes(w))
      if (isAllConversational) return false

      // Also if it doesn't contain any punctuation or capital letters typical of story prose
      const hasPunctuation = /[.!?,"'-]/.test(trimmed)
      if (!hasPunctuation) return false
    }

    return true
  }

  // 1. If [STORY] tags are present, parse by tag
  if (text.includes('[STORY]') || text.includes('[/STORY]')) {
    let remaining = text
    while (remaining.length > 0) {
      const storyStart = remaining.indexOf('[STORY]')
      if (storyStart === -1) {
        blocks.push({ type: 'chat', content: remaining })
        break
      }

      // Look for a matching [/STORY] closing tag
      const storyEnd = remaining.indexOf('[/STORY]', storyStart + 7)

      if (storyEnd !== -1) {
        const potentialContent = remaining.substring(storyStart + 7, storyEnd)
        if (isRealStoryBlock(potentialContent)) {
          // It's a real story block!
          if (storyStart > 0) {
            blocks.push({ type: 'chat', content: remaining.substring(0, storyStart) })
          }
          blocks.push({ type: 'story', title: 'Story Draft', content: potentialContent })
          remaining = remaining.substring(storyEnd + 8)
        } else {
          // Spurious/conversational mention. Consume the part up to the end of [/STORY] as chat
          const endIdx = storyEnd + 8
          blocks.push({ type: 'chat', content: remaining.substring(0, endIdx) })
          remaining = remaining.substring(endIdx)
        }
      } else {
        // No closing tag found. Let's see if the rest of the text is a real story block
        const potentialContent = remaining.substring(storyStart + 7)
        if (potentialContent.trim().length > 50 && isRealStoryBlock(potentialContent)) {
          if (storyStart > 0) {
            blocks.push({ type: 'chat', content: remaining.substring(0, storyStart) })
          }
          blocks.push({ type: 'story', title: 'Story Draft', content: potentialContent })
          break
        } else {
          // Treat as chat up to the end of the text
          blocks.push({ type: 'chat', content: remaining })
          break
        }
      }
    }

    // Merge consecutive chat blocks to keep formatting clean
    const mergedBlocks: ContentBlock[] = []
    for (const b of blocks) {
      if (b.content.trim().length === 0) continue
      const last = mergedBlocks[mergedBlocks.length - 1]
      if (last && last.type === 'chat' && b.type === 'chat') {
        last.content += b.content
      } else {
        mergedBlocks.push({ ...b })
      }
    }
    return mergedBlocks
  }

  // 2. Fallback: Parse using header detection to split text into chat and story blocks
  const lines = text.split(/\r?\n/)
  let currentBlockType: 'chat' | 'story' = 'chat'
  let currentBlockLines: string[] = []
  let currentTitle = ''

  const isDraftHeader = (line: string): boolean => {
    const clean = line.trim().toLowerCase()
    if (
      clean.includes('outline') ||
      clean.includes('plan') ||
      clean.includes('suggestion') ||
      clean.includes('structure')
    ) {
      return false
    }
    const keywords = ['draft', 'story', 'prose', 'copy', 'manuscript']
    const hasKeyword = keywords.some((k) => clean.includes(k))
    if (!hasKeyword) return false

    return (
      clean.startsWith('#') ||
      (clean.startsWith('**') && clean.endsWith('**')) ||
      (clean.startsWith('**') && clean.includes('**:')) ||
      (clean.endsWith(':') && clean.includes('**'))
    )
  }

  const isOutroLine = (line: string): boolean => {
    const clean = line.trim().toLowerCase()
    return (
      clean.startsWith('let me know') ||
      clean.startsWith('hope this') ||
      clean.startsWith('what do you think') ||
      clean.startsWith('we can expand') ||
      clean.startsWith('feel free to')
    )
  }

  const isOtherHeader = (line: string): boolean => {
    const clean = line.trim().toLowerCase()
    const isHeading = clean.startsWith('#')
    const isBoldHeader =
      (clean.startsWith('**') && clean.endsWith('**')) ||
      (clean.startsWith('**') && clean.includes('**:'))
    return (isHeading || isBoldHeader) && !isDraftHeader(line)
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    if (isDraftHeader(line)) {
      if (currentBlockLines.length > 0) {
        blocks.push({
          type: currentBlockType,
          title: currentBlockType === 'story' ? currentTitle : undefined,
          content: currentBlockLines.join('\n').trim()
        })
      }
      currentBlockType = 'story'
      currentTitle = line.replace(/[#*:]/g, '').trim() || 'Story Draft'
      currentBlockLines = []
    } else if (currentBlockType === 'story' && (isOutroLine(line) || isOtherHeader(line))) {
      if (currentBlockLines.length > 0) {
        blocks.push({
          type: 'story',
          title: currentTitle,
          content: currentBlockLines.join('\n').trim()
        })
      }
      currentBlockType = 'chat'
      currentTitle = ''
      currentBlockLines = [line]
    } else {
      currentBlockLines.push(line)
    }
  }

  if (currentBlockLines.length > 0) {
    blocks.push({
      type: currentBlockType,
      title: currentBlockType === 'story' ? currentTitle : undefined,
      content: currentBlockLines.join('\n').trim()
    })
  }

  return blocks.filter((b) => b.content.trim().length > 0)
}
